import React, { useState, useEffect } from "react";
import {
  BookOpen,
  Search,
  Clock,
  User,
  Globe,
  Loader,
  ImageOff,
  CheckCircle,
  PlusCircle,
  X,
  Sparkles,
  QrCode,
  CreditCard,
  Building,
  Upload,
  Image as ImageIcon,
  Copy,
  FileText,
  AlertCircle,
  ShieldCheck,
  Radio,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  lmsGetCourses,
  lmsGetCoursesByClassId,
  lmsGetStudentAllCourses,
  lmsGetPaymentConfigBySchoolId,
  lmsGetPaymentConfigs,
  lmsCreateEnrollments,
  lmsSubmitStudentCourseRequest,
  lmsGetLiveClasses,
} from "../api/lmsApi";
import { JoinModal } from "./LiveClasses";
import "../lms.css";

const getStudentData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const studentId = ud.student_id || ud.id || null;
    const schoolId = ud.school_id || ud.admin_id || ud.parent_id || null;
    const classId = ud.class_id || null;
    const studentKey = ud.student_id || ud.admin_id || "default_student";
    return { studentId, schoolId, classId, studentKey, userData: ud };
  } catch {
    return { studentId: null, schoolId: null, classId: null, studentKey: "default_student", userData: {} };
  }
};

const LEVEL_COLORS = {
  beginner: { bg: "#dcfce7", color: "#166534" },
  intermediate: { bg: "#fef9c3", color: "#854d0e" },
  advanced: { bg: "#fee2e2", color: "#991b1b" },
};

export default function StudentAvailableCourses() {
  const { studentId, schoolId, classId } = getStudentData();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterLevel, setFilterLevel] = useState("");

  // Modal & Enrollment States
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // Form States
  const [amountPaid, setAmountPaid] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);

  // QR States
  const [showQr, setShowQr] = useState(false);
  const [qrUrl, setQrUrl] = useState("");
  const [qrRemarks, setQrRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Live class states
  const [liveClasses, setLiveClasses] = useState([]);
  const [joinLiveItem, setJoinLiveItem] = useState(null);

  useEffect(() => {
    fetchCourses();
  }, [studentId, schoolId, classId]);

  // Fetch live classes for the school
  useEffect(() => {
    const extract = (res) => res.data?.data || (Array.isArray(res.data) ? res.data : []);
    const tryFetch = async () => {
      try {
        // Try with school_id first
        const res = await lmsGetLiveClasses(schoolId ? { school_id: schoolId } : {});
        let data = extract(res);
        // If no results and we had a school_id, try without filter
        if (!data.length && schoolId) {
          const res2 = await lmsGetLiveClasses({});
          data = extract(res2);
        }
        setLiveClasses(data);
      } catch {}
    };
    tryFetch();
  }, [schoolId]);

  // Find active live class for a course
  const getActiveLiveClass = (course) => {
    const now = new Date();
    const isActive = (lc) => {
      if (lc.status === "ended") return false;
      if (lc.status === "live") return true;
      if (!lc.scheduled_at) return false;
      const start = new Date(lc.scheduled_at);
      const end   = new Date(start.getTime() + (lc.duration_minutes || 60) * 60000);
      return now >= start && now <= end;
    };
    // 1. Exact class + subject match
    const byClassSubject = liveClasses.find((lc) =>
      isActive(lc) &&
      course.class_id && course.subject_id &&
      String(lc.class_id) === String(course.class_id) &&
      String(lc.subject_id) === String(course.subject_id)
    );
    if (byClassSubject) return byClassSubject;
    // 2. Same class, live class has no subject restriction
    const byClass = liveClasses.find((lc) =>
      isActive(lc) && course.class_id &&
      String(lc.class_id) === String(course.class_id) && !lc.subject_id
    );
    if (byClass) return byClass;
    return null;
  };

  const fetchCourses = async () => {
    setLoading(true);
    try {
      if (studentId) {
        const res = await lmsGetStudentAllCourses(studentId, schoolId, classId);
        const data = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setCourses(data);
      } else {
        const res = classId
          ? await lmsGetCoursesByClassId(classId, schoolId)
          : await lmsGetCourses({ school_id: schoolId });
        const data = res.data?.data || res.data || [];
        if (Array.isArray(data)) {
          setCourses(data);
        }
      }
    } catch (error) {
      console.error("Error fetching available courses:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEnrollModal = async (course) => {
    setSelectedCourse(course);
    const payableAmount = course.discount_price
      ? parseFloat(course.discount_price)
      : parseFloat(course.price || 0);
    setAmountPaid(payableAmount > 0 ? String(payableAmount) : "0");
    setTransactionId("");
    setScreenshotFile(null);
    setScreenshotPreview(null);
    setShowQr(false);
    setQrUrl("");
    setQrRemarks("");

    // Fetch Payment Config for School
    setLoadingConfig(true);
    try {
      let cfg = null;
      try {
        const res = await lmsGetPaymentConfigBySchoolId(schoolId);
        cfg = res.data?.data || res.data || null;
      } catch {
        const res = await lmsGetPaymentConfigs({ school_id: schoolId });
        const list = res.data?.data || (Array.isArray(res.data) ? res.data : []);
        cfg = list[0] || null;
      }
      setPaymentConfig(cfg);
    } catch (err) {
      console.error("Error fetching payment configuration:", err);
      toast.error("Could not load school payment configuration.");
      setPaymentConfig(null);
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleGenerateQr = () => {
    if (!selectedCourse) return;

    const upiId = paymentConfig?.upi_id || "docmaster@upi";
    const upiName = paymentConfig?.upi_account_name || "DocMaster School";
    const amountToBePaid = selectedCourse.discount_price
      ? parseFloat(selectedCourse.discount_price)
      : parseFloat(selectedCourse.price || 0);
    const courseId = selectedCourse.id || selectedCourse.course_id;

    // Pass upi_id, student_id, course_id, school_id as remarks
    const remarks = `upi_id:${upiId},student_id:${studentId || 0},course_id:${courseId},school_id:${schoolId || 0}`;
    setQrRemarks(remarks);

    const upiUri = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(
      upiName
    )}&am=${amountToBePaid}&tn=${encodeURIComponent(remarks)}&cu=INR`;

    const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(
      upiUri
    )}`;

    setQrUrl(qrImageUrl);
    setShowQr(true);
    toast.info("UPI QR Code generated! Scan with any UPI App.");
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshotFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopyText = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`Copied ${label} to clipboard!`);
  };

  const handleSubmitEnrollment = async (e) => {
    e.preventDefault();

    if (!transactionId.trim()) {
      toast.error("Please enter the Transaction Number / UTR ID");
      return;
    }

    if (!screenshotFile && !screenshotPreview) {
      toast.error("Please upload the payment screenshot");
      return;
    }

    setSubmitting(true);
    try {
      const courseId = selectedCourse.id || selectedCourse.course_id;
      const remarksStr =
        qrRemarks ||
        `upi_id:${paymentConfig?.upi_id || ""},student_id:${studentId || 0},course_id:${courseId},school_id:${schoolId || 0}`;

      const formData = new FormData();
      formData.append("school_id", schoolId);
      formData.append("student_id", studentId);
      formData.append("course_id", courseId);
      formData.append("fee_paid", "yes");
      formData.append("amount", parseFloat(amountPaid));
      formData.append("payment_method", "upi_qr");
      formData.append("transaction_id", transactionId.trim());
      formData.append("transaction_number", transactionId.trim());
      formData.append("remarks", remarksStr);
      formData.append("is_student_request", "true");
      formData.append("status", "pending");

      if (screenshotFile) {
        formData.append("screenshot", screenshotFile);
      }

      await lmsSubmitStudentCourseRequest(formData);

      toast.info(`Enrollment request for "${selectedCourse.name}" submitted! Status is pending verification.`);

      setSelectedCourse(null);
      fetchCourses();
    } catch (err) {
      console.error("Error submitting enrollment:", err);
      toast.error(err.response?.data?.message || "Failed to submit enrollment payment.");
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = courses.filter((c) => {
    const matchesSearch =
      !search ||
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      (c.description && c.description.toLowerCase().includes(search.toLowerCase()));
    const matchesLevel = !filterLevel || c.level === filterLevel;
    return matchesSearch && matchesLevel;
  });

  return (
    <div>
      {/* Search & Filter Controls */}
      <div className="crs-filters" style={{ display: "flex", gap: "16px", marginBottom: "24px", alignItems: "center" }}>
        <div className="crs-search-wrap" style={{ flex: 1, position: "relative" }}>
          <Search size={16} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
          <input
            placeholder="Search available courses by title or keyword..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 14px 10px 40px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              fontSize: "14px",
            }}
          />
        </div>
        <select
          className="crs-filter-select"
          value={filterLevel}
          onChange={(e) => setFilterLevel(e.target.value)}
          style={{
            padding: "10px 16px",
            borderRadius: "8px",
            border: "1px solid #cbd5e1",
            fontSize: "14px",
            background: "#ffffff",
          }}
        >
          <option value="">All Levels</option>
          <option value="beginner">Beginner</option>
          <option value="intermediate">Intermediate</option>
          <option value="advanced">Advanced</option>
        </select>
        <span style={{ fontSize: "14px", color: "#64748b", fontWeight: "500" }}>
          {filtered.length} {filtered.length === 1 ? "Course" : "Courses"} Available
        </span>
      </div>

      {/* Course Cards Grid */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0" }}>
          <Loader size={32} className="spinner" style={{ margin: "0 auto 12px" }} />
          <p style={{ color: "#64748b", fontSize: "15px" }}>Loading available courses...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 20px",
            background: "#ffffff",
            borderRadius: "12px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <BookOpen size={48} color="#94a3b8" style={{ margin: "0 auto 12px" }} />
          <h3 style={{ fontSize: "16px", fontWeight: "600", color: "#334155" }}>
            No available courses found
          </h3>
          <p style={{ fontSize: "14px", color: "#94a3b8", marginTop: "4px" }}>
            {search || filterLevel
              ? "Try adjusting your search criteria"
              : "No new courses are currently published for your school."}
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: "24px",
          }}
        >
          {filtered.map((course) => {
            const lvl = LEVEL_COLORS[course.level] || LEVEL_COLORS.beginner;
            const price = parseFloat(course.price) || 0;
            const discountPrice = parseFloat(course.discount_price);
            const hasEnrollment = Boolean(course.is_enrolled) || course.is_enrolled === 1 || course.is_enrolled === true;

            const isEnrolled =
              hasEnrollment &&
              (course.payment_status === "paid" ||
                course.payment_status === "not_required" ||
                course.enrollment_status === "active" ||
                course.is_free === 1 ||
                course.is_free === true ||
                (!course.payment_status && !course.enrollment_status));

            const isPending =
              hasEnrollment &&
              !isEnrolled &&
              (course.payment_status === "pending" ||
                course.enrollment_status === "pending" ||
                course.payment_status === "under_verification");

            const activeLiveClass = getActiveLiveClass(course);

            return (
              <div
                key={course.id || course.course_id}
                style={{
                  background: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                {/* Thumbnail */}
                <div
                  style={{
                    height: "160px",
                    background: "#f1f5f9",
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {course.thumbnail_url ? (
                    <img
                      src={course.thumbnail_url}
                      alt={course.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <ImageOff size={32} color="#94a3b8" />
                  )}
                  <span
                    style={{
                      position: "absolute",
                      top: "12px",
                      left: "12px",
                      background: lvl.bg,
                      color: lvl.color,
                      padding: "4px 10px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "600",
                      textTransform: "capitalize",
                    }}
                  >
                    {course.level || "beginner"}
                  </span>
                  {course.is_free && (
                    <span
                      style={{
                        position: "absolute",
                        top: "12px",
                        right: "12px",
                        background: "#22c55e",
                        color: "#ffffff",
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "700",
                      }}
                    >
                      FREE
                    </span>
                  )}
                </div>

                {/* Body */}
                <div style={{ padding: "20px", flex: 1, display: "flex", flexDirection: "column" }}>
                  <h3
                    style={{
                      fontSize: "17px",
                      fontWeight: "600",
                      color: "#0f172a",
                      marginBottom: "8px",
                      lineHeight: "1.4",
                    }}
                  >
                    {course.name}
                  </h3>
                  {course.description && (
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "16px",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        lineHeight: "1.5",
                      }}
                    >
                      {course.description}
                    </p>
                  )}

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      fontSize: "12px",
                      color: "#475569",
                      marginTop: "auto",
                      marginBottom: "16px",
                    }}
                  >
                    {course.instructor_name && (
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <User size={13} color="#94a3b8" />
                        {course.instructor_name}
                      </span>
                    )}
                    {course.duration_days && (
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Clock size={13} color="#94a3b8" />
                        {course.duration_days} Days
                      </span>
                    )}
                    {course.language && (
                      <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                        <Globe size={13} color="#94a3b8" />
                        {course.language}
                      </span>
                    )}
                  </div>

                  {/* Card Action */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      paddingTop: "14px",
                      borderTop: "1px solid #f1f5f9",
                    }}
                  >
                    <div>
                      {course.is_free ? (
                        <span style={{ fontSize: "16px", fontWeight: "700", color: "#16a34a" }}>
                          Free
                        </span>
                      ) : (
                        <>
                          {discountPrice ? (
                            <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                              <span style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                                ₹{discountPrice.toFixed(2)}
                              </span>
                              <span style={{ fontSize: "13px", color: "#94a3b8", textDecoration: "line-through" }}>
                                ₹{price.toFixed(2)}
                              </span>
                            </div>
                          ) : (
                            <span style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                              {price > 0 ? `₹${price.toFixed(2)}` : "Free"}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {isEnrolled ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {activeLiveClass ? (
                          <button
                            onClick={() => setJoinLiveItem(activeLiveClass)}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "6px",
                              padding: "8px 14px", borderRadius: "8px", border: "none",
                              background: "#16a34a", color: "#fff", fontWeight: "700",
                              fontSize: "13px", cursor: "pointer",
                              animation: "pulse 1.5s infinite",
                            }}
                          >
                            <Radio size={14} /> Join Live
                          </button>
                        ) : (
                          <span
                            style={{
                              display: "inline-flex", alignItems: "center", gap: "6px",
                              padding: "8px 14px", borderRadius: "8px",
                              background: "#ecfdf5", color: "#059669",
                              fontWeight: "600", fontSize: "13px",
                            }}
                          >
                            <CheckCircle size={15} /> Enrolled
                          </span>
                        )}
                      </div>
                    ) : isPending ? (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          padding: "8px 14px",
                          borderRadius: "8px",
                          background: "#fef3c7",
                          color: "#d97706",
                          fontWeight: "600",
                          fontSize: "13px",
                        }}
                      >
                        <Clock size={15} />
                        Pending Verification
                      </span>
                    ) : (() => {
                      const isExpired = course.end_date && new Date(course.end_date) < new Date();
                      return isExpired ? (
                        <span style={{
                          display: "inline-flex", alignItems: "center", gap: "6px",
                          padding: "8px 14px", borderRadius: "8px",
                          background: "#f1f5f9", color: "#94a3b8",
                          fontWeight: "600", fontSize: "13px",
                        }}>
                          Course Ended
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleOpenEnrollModal(course)}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: "6px",
                            padding: "8px 16px", borderRadius: "8px",
                            background: "#1c498d", color: "#ffffff",
                            border: "none", fontWeight: "600", fontSize: "13px", cursor: "pointer",
                          }}
                        >
                          <PlusCircle size={15} />
                          Enroll Now
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Join Live Class Modal */}
      {joinLiveItem && (
        <JoinModal liveClass={joinLiveItem} onClose={() => setJoinLiveItem(null)} studentMode />
      )}

      {/* Course Enrollment & Payment Modal */}
      {selectedCourse && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setSelectedCourse(null)}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              maxWidth: "680px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{ padding: "20px 24px", background: "#1c498d", color: "#ffffff", position: "relative" }}>
              <button
                onClick={() => setSelectedCourse(null)}
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "18px",
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  color: "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <X size={18} />
              </button>
              <h2 style={{ fontSize: "19px", fontWeight: "700", margin: 0 }}>
                Course Enrollment & Payment
              </h2>
              <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: "14px" }}>
                {selectedCourse.name}
              </p>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "24px" }}>
              {/* Highlighted Amount to be Paid */}
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: "10px",
                  padding: "16px 20px",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <span style={{ fontSize: "13px", color: "#15803d", fontWeight: "600" }}>
                    Course Fee Summary
                  </span>
                  <div style={{ fontSize: "18px", fontWeight: "800", color: "#166534", marginTop: "2px" }}>
                    Amount to be Paid: ₹
                    {(selectedCourse.discount_price
                      ? parseFloat(selectedCourse.discount_price)
                      : parseFloat(selectedCourse.price || 0)
                    ).toLocaleString("en-IN")}
                  </div>
                </div>

                {selectedCourse.discount_price && selectedCourse.price > selectedCourse.discount_price && (
                  <div
                    style={{
                      background: "#dcfce7",
                      color: "#166534",
                      padding: "4px 10px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "700",
                    }}
                  >
                    Special Discounted Price!
                  </div>
                )}
              </div>

              {/* School Payment Account Details */}
              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "16px",
                  background: "#f8fafc",
                  marginBottom: "20px",
                }}
              >
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "700",
                    color: "#0f172a",
                    marginBottom: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Building size={16} color="#1c498d" />
                  School Payment Account & Banking Details
                </h4>

                {loadingConfig ? (
                  <div style={{ textAlign: "center", padding: "16px 0", color: "#64748b", fontSize: "13px" }}>
                    <Loader size={18} className="spinner" style={{ margin: "0 auto 6px" }} />
                    Loading school payment configuration...
                  </div>
                ) : paymentConfig ? (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", fontSize: "13px" }}>
                    {paymentConfig.upi_id && (
                      <div>
                        <span style={{ color: "#64748b" }}>UPI ID:</span>{" "}
                        <strong style={{ color: "#0f172a" }}>{paymentConfig.upi_id}</strong>
                        <button
                          type="button"
                          onClick={() => handleCopyText(paymentConfig.upi_id, "UPI ID")}
                          style={{ border: "none", background: "none", marginLeft: "6px", cursor: "pointer" }}
                        >
                          <Copy size={13} color="#1c498d" />
                        </button>
                      </div>
                    )}
                    {paymentConfig.upi_account_name && (
                      <div>
                        <span style={{ color: "#64748b" }}>UPI Account Name:</span>{" "}
                        <strong style={{ color: "#0f172a" }}>{paymentConfig.upi_account_name}</strong>
                      </div>
                    )}
                    {paymentConfig.bank_name && (
                      <div>
                        <span style={{ color: "#64748b" }}>Bank Name:</span>{" "}
                        <strong style={{ color: "#0f172a" }}>{paymentConfig.bank_name}</strong>
                      </div>
                    )}
                    {paymentConfig.account_holder_name && (
                      <div>
                        <span style={{ color: "#64748b" }}>Account Holder:</span>{" "}
                        <strong style={{ color: "#0f172a" }}>{paymentConfig.account_holder_name}</strong>
                      </div>
                    )}
                    {paymentConfig.account_number && (
                      <div>
                        <span style={{ color: "#64748b" }}>Account Number:</span>{" "}
                        <strong style={{ color: "#0f172a" }}>{paymentConfig.account_number}</strong>
                      </div>
                    )}
                    {paymentConfig.ifsc_code && (
                      <div>
                        <span style={{ color: "#64748b" }}>IFSC Code:</span>{" "}
                        <strong style={{ color: "#0f172a" }}>{paymentConfig.ifsc_code}</strong>
                      </div>
                    )}
                    {paymentConfig.branch_name && (
                      <div>
                        <span style={{ color: "#64748b" }}>Branch:</span>{" "}
                        <strong style={{ color: "#0f172a" }}>{paymentConfig.branch_name}</strong>
                      </div>
                    )}
                  </div>
                ) : (
                  <p style={{ fontSize: "13px", color: "#64748b", margin: 0 }}>
                    Standard School Payment Account. Contact administration for details.
                  </p>
                )}

                {/* Generate QR Button */}
                <div style={{ marginTop: "14px", paddingTop: "12px", borderTop: "1px dashed #cbd5e1" }}>
                  <button
                    type="button"
                    onClick={handleGenerateQr}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "8px 16px",
                      borderRadius: "8px",
                      background: "#1c498d",
                      color: "#ffffff",
                      border: "none",
                      fontWeight: "600",
                      fontSize: "13px",
                      cursor: "pointer",
                    }}
                  >
                    <QrCode size={16} />
                    Generate UPI QR Code
                  </button>
                </div>
              </div>

              {/* Display Generated QR Code */}
              {showQr && qrUrl && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    background: "#f1f5f9",
                    borderRadius: "12px",
                    marginBottom: "20px",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  <h4 style={{ fontSize: "14px", fontWeight: "700", color: "#0f172a", marginBottom: "8px" }}>
                    Scan & Pay via UPI App
                  </h4>
                  <img
                    src={qrUrl}
                    alt="UPI QR Code"
                    style={{
                      width: "180px",
                      height: "180px",
                      borderRadius: "8px",
                      border: "2px solid #ffffff",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                      marginBottom: "10px",
                    }}
                  />
                  <div style={{ fontSize: "12px", color: "#475569", wordBreak: "break-all" }}>
                    <strong>UPI Remarks passed:</strong> <br />
                    <span style={{ fontFamily: "monospace", color: "#1c498d", background: "#ffffff", padding: "2px 6px", borderRadius: "4px" }}>
                      {qrRemarks}
                    </span>
                  </div>
                </div>
              )}

              {/* Enrollment Payment Form */}
              <form onSubmit={handleSubmitEnrollment} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  {/* Field 1: Amount Paid */}
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                      Amount Paid (₹)
                    </label>
                    <input
                      type="text"
                      disabled
                      readOnly
                      value={`₹${parseFloat(amountPaid || 0).toFixed(2)}`}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        fontSize: "14px",
                        outline: "none",
                        background: "#e2e8f0",
                        color: "#1c498d",
                        fontWeight: "700",
                        cursor: "not-allowed",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>

                  {/* Field 2: Transaction Number / UTR ID */}
                  <div>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                      Transaction Number / UTR ID <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. TXN123456789 / UTR"
                      value={transactionId}
                      onChange={(e) => setTransactionId(e.target.value)}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: "8px",
                        border: "1px solid #cbd5e1",
                        fontSize: "14px",
                        outline: "none",
                      }}
                    />
                  </div>
                </div>

                {/* Field 3: Payment Screenshot Upload */}
                <div>
                  <label style={{ display: "block", fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px" }}>
                    Payment Screenshot / Receipt Proof <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <div
                    style={{
                      border: "2px dashed #cbd5e1",
                      borderRadius: "8px",
                      padding: "16px",
                      textAlign: "center",
                      background: "#f8fafc",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      required={!screenshotPreview}
                      onChange={handleFileChange}
                      style={{
                        position: "absolute",
                        inset: 0,
                        opacity: 0,
                        cursor: "pointer",
                        width: "100%",
                      }}
                    />
                    {screenshotPreview ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "12px" }}>
                        <img
                          src={screenshotPreview}
                          alt="Screenshot Proof"
                          style={{ width: "60px", height: "60px", objectFit: "cover", borderRadius: "6px" }}
                        />
                        <div style={{ textAlign: "left" }}>
                          <span style={{ fontSize: "13px", fontWeight: "600", color: "#16a34a", display: "flex", alignItems: "center", gap: "4px" }}>
                            <ShieldCheck size={14} /> Screenshot Uploaded!
                          </span>
                          <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>Click to replace file</p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Upload size={24} color="#1c498d" style={{ margin: "0 auto 6px" }} />
                        <span style={{ fontSize: "13px", color: "#475569", fontWeight: "500" }}>
                          Click to upload payment screenshot (JPG/PNG)
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Footer Buttons */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "12px" }}>
                  <button
                    type="button"
                    onClick={() => setSelectedCourse(null)}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      background: "#ffffff",
                      color: "#475569",
                      fontWeight: "600",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      padding: "10px 22px",
                      borderRadius: "8px",
                      border: "none",
                      background: "#1c498d",
                      color: "#ffffff",
                      fontWeight: "600",
                      fontSize: "14px",
                      cursor: submitting ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      opacity: submitting ? 0.7 : 1,
                    }}
                  >
                    {submitting ? (
                      <>
                        <Loader size={16} className="spinner" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} />
                        Confirm & Submit Payment
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
